/**
 * The trial kept only on this device (29 Oct 2026).
 *
 * Before a parent has given consent, a child can try Word Sprout, and what
 * they do is remembered here, in this browser's storage, and nowhere else:
 * the school year a grown-up picked, which page of words is next, the days
 * they practised, and for each word met how often it was right or wrong.
 * No name, and nothing is sent: the word pages come from the server with no
 * cookie (api.tryWords), and marking happens on the device (lib/marking.ts).
 *
 * After consent, a grown-up can add the trial's words to a child's list
 * (Settings, TrialCarryOver); only which words, and which were missed, are
 * sent then. "Delete it" clears everything here at once.
 * research/trial-and-consent/08-on-device-trial.md.
 */
const KEY = 'ws-try-v1'
// Enough for two weeks of six words a day, with room to spare.
const MAX_WORDS = 300

export interface TryWord { id: string; word: string; right: number; wrong: number; last: string }
export interface TryState {
  year: number | null
  /** The next page of words to ask the server for. */
  page: number
  started: string
  /** Days (YYYY-MM-DD, this device's clock) with a finished page. */
  days: string[]
  words: Record<string, TryWord>
}

const fresh = (): TryState => ({ year: null, page: 0, started: today(), days: [], words: {} })

export function today(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const count = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0)
const isDay = (d: unknown): d is string => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)

/**
 * Whatever is stored, as a well-formed TryState. Anything of the wrong shape
 * (an older version, a hand edit, an extension) is dropped rather than
 * allowed to crash the trial (Codex round 16).
 */
function tidy(raw: unknown): TryState {
  const s = fresh()
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return s
  const r = raw as Record<string, unknown>
  if (typeof r.year === 'number' && Number.isInteger(r.year) && r.year >= 0 && r.year <= 13) s.year = r.year
  s.page = count(r.page)
  if (isDay(r.started)) s.started = r.started
  if (Array.isArray(r.days)) s.days = r.days.filter(isDay)
  if (r.words && typeof r.words === 'object' && !Array.isArray(r.words)) {
    for (const [id, w] of Object.entries(r.words as Record<string, unknown>)) {
      if (!w || typeof w !== 'object') continue
      const x = w as Record<string, unknown>
      if (typeof x.word !== 'string') continue
      s.words[id] = { id, word: x.word, right: count(x.right), wrong: count(x.wrong),
        last: isDay(x.last) ? x.last : s.started }
    }
  }
  return s
}

export const tryStore = {
  load(): TryState {
    try {
      const raw = localStorage.getItem(KEY)
      if (!raw) return fresh()
      return tidy(JSON.parse(raw))
    } catch {
      return fresh()
    }
  },
  save(s: TryState): void {
    try { localStorage.setItem(KEY, JSON.stringify(s)) } catch { /* private window: forget, as promised */ }
  },
  /** Anything kept here at all. */
  has(): boolean {
    const s = tryStore.load()
    return s.year !== null || Object.keys(s.words).length > 0
  },
  clear(): void {
    try { localStorage.removeItem(KEY) } catch { /* nothing to do */ }
  },
  setYear(year: number): TryState {
    const s = { ...tryStore.load(), year }
    tryStore.save(s)
    return s
  },
  /** One marked answer. `correct` null is a card with nothing to mark. */
  record(id: string, word: string, correct: boolean | null): void {
    const s = tryStore.load()
    const w = s.words[id] ?? { id, word, right: 0, wrong: 0, last: today() }
    if (correct === true) w.right += 1
    if (correct === false) w.wrong += 1
    w.last = today()
    s.words[id] = w
    const ids = Object.keys(s.words)
    if (ids.length > MAX_WORDS) {
      // The oldest go first.
      ids.sort((a, b) => (s.words[a]!.last < s.words[b]!.last ? -1 : 1))
      for (const old of ids.slice(0, ids.length - MAX_WORDS)) delete s.words[old]
    }
    tryStore.save(s)
  },
  /** A page finished: the next one tomorrow, and today counted. */
  finishPage(): TryState {
    const s = tryStore.load()
    s.page += 1
    if (!s.days.includes(today())) s.days.push(today())
    tryStore.save(s)
    return s
  },
  /** For a child's list, after consent: which words, and which were missed. */
  forCarryOver(): { word_sense_id: string; missed: boolean }[] {
    return Object.values(tryStore.load().words).map((w) => ({ word_sense_id: w.id, missed: w.wrong > 0 }))
  },
}
